import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import NuestrasClases from '../components/NuestrasClases';
import Gallery from '../components/Gallery';
import Contacto from '../components/Contacto';
import BeyondIntro from '../components/BeyondIntro';
import Profesionales, { parseSpecialties } from '../components/Profesionales';
import { fetchPublicProfessionals } from '../database/databaseProfiles';
import { Link as RouterLink } from 'react-router-dom';
import { Link as ScrollLink, scroller } from 'react-scroll';

const BUBBLE_COLORS = [
  'bg-[#D9A441] hover:bg-[#B9842B]', // dorado
  'bg-[#7D8552] hover:bg-[#636A3F]', // oliva
  'bg-[#8C5E3C] hover:bg-[#6B4226]', // castaño
  'bg-[#7A3B3B] hover:bg-[#5E2C2C]', // vino
  'bg-[#4F6D4F] hover:bg-[#3C543C]', // verde bosque
  'bg-[#B8963E] hover:bg-[#967A30]', // ocre
];

const Home = () => {
  const [ready, setReady] = useState(false);
  const [specialtyPeople, setSpecialtyPeople] = useState([]);
  const [activeFilter, setActiveFilter] = useState('Todos');

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    (async () => {
      const data = await fetchPublicProfessionals();
      setSpecialtyPeople(data || []);
    })();
  }, []);

  const specialties = useMemo(
    () => Array.from(new Set(specialtyPeople.flatMap((p) => parseSpecialties(p.specialty)))),
    [specialtyPeople]
  );

  const goToSpecialty = (specialty) => {
    setActiveFilter(specialty);
    scroller.scrollTo('profesionales', { smooth: true, duration: 500 });
  };

  return (
    <Layout page="home" hasServices={specialties.length > 0}>
      <section
        id="inicio"
        className="relative min-h-[92vh] md:min-h-screen flex flex-col items-center justify-center text-center px-6 pt-16 md:pt-20 pb-28 md:pb-36 overflow-hidden"
        style={{
          background: 'radial-gradient(circle at 50% 35%, #FFF8F0, #F6EFE6 70%)',
        }}
      >
        <div
          className={`relative z-[2] flex flex-col items-center max-w-2xl transition-all duration-700 ${
            ready ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
          }`}
        >
          <div className="w-40 sm:w-52 md:w-60 aspect-square overflow-hidden mb-1 mx-auto">
            <img
              src="/icons/origin-logo.png"
              alt="Origin Beyond Pilates"
              className="w-full h-full object-contain scale-150 select-none"
            />
          </div>

          <p className="text-entrance font-serif text-terracotta text-xl sm:text-2xl mb-2">
            fuerza · control · movimiento
          </p>

          <p className="fade-in-up [animation-delay:450ms] text-ink/80 text-base sm:text-lg">
            Sesiones personalizadas en grupos reducidos para que encuentres
            equilibrio, fuerza y bienestar en cada movimiento.
          </p>

          {/* CTAs */}
          <div className="fade-in-up [animation-delay:650ms] mt-5 sm:mt-6 flex flex-wrap justify-center gap-3 sm:gap-4">
            <RouterLink
              to="/reserve"
              className="inline-block border border-terracotta text-terracotta hover:bg-terracotta hover:text-cream px-8 py-3.5 rounded-full text-xs uppercase tracking-[0.25em] transition"
            >
              Reserva tu clase
            </RouterLink>
            {specialties.length > 0 && (
              <ScrollLink
                to="profesionales"
                smooth
                duration={500}
                className="cursor-pointer inline-block bg-terracotta text-cream hover:bg-terracotta-dark px-8 py-3.5 rounded-full text-xs uppercase tracking-[0.25em] transition"
              >
                Ver servicios
              </ScrollLink>
            )}
          </div>

          {specialties.length > 0 && (
            <>
              <p className="fade-in-up [animation-delay:850ms] text-ink/70 text-sm sm:text-base mt-5 sm:mt-6">
                Pilates y mucho más...
              </p>

              <div className="fade-in-up [animation-delay:1050ms] mt-3 flex flex-wrap justify-center gap-2.5 max-w-lg">
                {specialties.map((specialty, i) => (
                  <button
                    key={specialty}
                    onClick={() => goToSpecialty(specialty)}
                    className={`${BUBBLE_COLORS[i % BUBBLE_COLORS.length]} cursor-pointer text-white text-xs sm:text-sm font-medium px-4 py-2 rounded-full shadow-sm hover:scale-105 hover:shadow-md transition`}
                  >
                    {specialty}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* wave divider */}
        <svg
          className="absolute left-0 bottom-[-1px] w-full h-24 md:h-40"
          viewBox="0 0 1440 170"
          preserveAspectRatio="none"
        >
          <path fill="#EAD9C6" opacity=".55" d="M0 70C240 10 480 130 720 80s480-70 720 0V170H0z" />
          <path fill="#C4502A" opacity=".18" d="M0 100C300 40 520 150 780 100s430-60 660 10V170H0z" />
          <path fill="#EAD9C6" d="M0 130C260 90 520 170 800 130s420-40 640 0V170H0z" />
        </svg>
      </section>

      <BeyondIntro />
      <Gallery />
      <NuestrasClases className="classes" />
      <Profesionales activeFilter={activeFilter} onFilterChange={setActiveFilter} />
      <Contacto />
    </Layout>
  );
};

export default Home;
